import React, { useState, useEffect, useRef } from "react";
import { Carousel } from "primereact/carousel";
import SvgIcon from "@Components/icons/SvgIcon";
import { getVideoList } from "../../services/Home.service";
import { useAppSelector } from "@Src/app/hooks";
import { VideoCard } from "@Src/components/VideoCard";

const VideoTutorialsSlider = ({ filters = {}, playInPlace = false, onDataLoaded, usePublicApi = false }) => {
  const [videoTutorials, setVideoTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const previousFiltersRef = useRef(null);
  const hasFetchedRef = useRef(false);

  const ITEMS_PER_PAGE = 3;

  // Get course preferences and login status from Redux
  const coursePreferences = useAppSelector((state) => state.coursePreferences);
  const isLoggedIn = useAppSelector((state) => state.login.isLoggedIn);

  const fetchVideos = async (pageNumber = 1, append = false) => {
    try {
      setLoading(true);
      setError(null);

      // Extract IDs from course preferences
      const boardId = filters.boardId || coursePreferences?.boardDetails?.[0]?.Id;
      const classId = filters.classId || coursePreferences?.classDetails?.[0]?.Id;
      const subjectIds = filters.subjectId ||
        coursePreferences?.subjectDetails?.map((subject) => subject.Id) || [];

      // Determine effective login state (force logged out if usePublicApi is true)
      const effectiveIsLoggedIn = usePublicApi ? false : isLoggedIn;

      // Capping logic: Only cap at 9 for logged-in users (Dashboard/Home)
      // For guest users (logged out), show everything available
      // const MAX_TOTAL_ITEMS = effectiveIsLoggedIn ? 9 : 100;
      const MAX_TOTAL_ITEMS = 12;
      // Prepare filter data
      const filterData = {
        page: 1,
        limit: MAX_TOTAL_ITEMS,
        ...(boardId && { boardId }),
        ...(classId && { classId }),
        ...(subjectIds.length > 0 && { subjectId: subjectIds }),
        ...(filters.chapterId && { chapterId: filters.chapterId }),
        ...(filters.topicId && { topicId: filters.topicId })
      };

      const response = await getVideoList(filterData, effectiveIsLoggedIn);

      if (response.success && response.data?.result_data) {
        // Handle total records
        const actualTotalAvailable = response.data.total_record || 0;
        const finalTotal = effectiveIsLoggedIn ? Math.min(actualTotalAvailable, MAX_TOTAL_ITEMS) : actualTotalAvailable;
        setTotalRecords(finalTotal);

        // Handle items
        const items = effectiveIsLoggedIn ? response.data.result_data.slice(0, MAX_TOTAL_ITEMS) : response.data.result_data;
        setVideoTutorials(items);
        if (items.length > 0 && onDataLoaded) onDataLoaded(items);
      } else {
        setVideoTutorials([]);
        if (onDataLoaded) onDataLoaded([]);
      }
    } catch (err) {
      console.error("Error fetching videos:", err);
      setError("Failed to load videos. Please try again later.");
      setVideoTutorials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentFilters = JSON.stringify({ filters, coursePreferences });

    // Fetch on first mount or when filters actually change
    if (!hasFetchedRef.current || previousFiltersRef.current !== currentFilters) {
      previousFiltersRef.current = currentFilters;
      hasFetchedRef.current = true;
      fetchVideos(1, false);
    }
  }, [filters, coursePreferences]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1200);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1200);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const template = (item) => {
    if (!item || !item.VideoId) {
      const skeletonHeight = isMobile ? '380px' : '400px';
      const skeletonWidth = isMobile ? '100%' : '400px';

      return (
        <div className='videotutorials-item tw-h-full'>
          <div className="tw-rounded-2xl tw-overflow-hidden tw-shadow-lg tw-flex tw-flex-col tw-bg-white tw-border tw-border-gray-100"
            style={{ height: skeletonHeight, maxWidth: skeletonWidth, margin: '0 auto' }}>
            <div className={`tw-w-full ${isMobile ? 'tw-h-48' : 'tw-h-56'} tw-bg-gray-200 tw-animate-pulse tw-flex-shrink-0`} />
            <div className="tw-p-5 tw-flex tw-flex-col tw-gap-3">
              <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse" />
              <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-3/4" />
            </div>
          </div>
        </div>
      );
    }

    // Dynamic sizing based on screen type
    const cardHeight = isMobile ? 380 : 450;
    const cardMaxWidth = isMobile ? '100%' : 450;

    return (
      <div className='videotutorials-item tw-h-full'>
        <VideoCard
          videoId={item.VideoId}
          videoTitle={item.VideoTitle}
          description={item.Description}
          videoLink={item.TrailerVideoLink || item.VideoLink}
          fullVideoLink={item.VideoLink}
          video={item}
          playInPlace={playInPlace}
          isCurrentPlaying={playingVideoId === item.VideoId}
          onPlay={() => setPlayingVideoId(item.VideoId)}
          height={cardHeight}
          maxWidth={cardMaxWidth}
        />
      </div>
    );
  };

  // Determine what to show in the carousel
  let displayItems = [];

  if (loading && videoTutorials.length === 0) {
    // Show 3 skeletons initially while loading
    displayItems = Array(3).fill(null);
  } else {
    // Show only actual videos, no placeholders
    displayItems = [...videoTutorials];
  }

  // Error state within carousel context
  if (error && videoTutorials.length === 0) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
        <p className="tw-text-red-600">{error}</p>
      </div>
    );
  }

  if (!loading && videoTutorials.length === 0 && totalRecords === 0) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
        <p className="tw-text-gray-600">Coming soon...</p>
      </div>
    );
  }

  // Calculate if we should show indicators and navigators based on video count
  const shouldShowControls = displayItems.length > 3;
  // Enable circular only if we have more than 3 videos (more than 1 page)
  const shouldBeCircular = displayItems.length > 3;

  return (
    <div className="w-full">
      <Carousel
        value={displayItems}
        itemTemplate={template}
        numVisible={3}
        numScroll={1}
        circular={shouldBeCircular}
        autoplayInterval={0} // Disable autoplay completely
        showIndicators={shouldShowControls}
        showNavigators={shouldShowControls}
        responsiveOptions={[
          { breakpoint: "1400px", numVisible: 3, numScroll: 1 },
          { breakpoint: "1200px", numVisible: 2, numScroll: 1 },
          { breakpoint: "768px", numVisible: 1, numScroll: 1 }
        ]}
      />
    </div>
  );
};

export default VideoTutorialsSlider;
